import './TaxesTiles.scss';

import { bool, object } from 'prop-types';
import React, { Fragment, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import BeaconDisclosureModal from 'components/shared/DisclosureModal/DisclosureModal';
import { Dollar } from 'components/shared/Text/Text';
import Content from 'services/Content';
import { getDefaultTaxYear } from 'state/taxes/TaxSelectors';
import { getIRAContributionLimit } from 'state/contributions/IRAContributionLimitSelectors';
import { CURRENT_YEAR, PRIOR_YEAR } from 'services/Dates';
import { isDesktopLayout, isMobileLayout } from 'state/view/ViewSelectors';
import { AAMBeaconSegmentedControls, AAMBeaconSegmentedControl } from 'components/shared/Beacon';
import Disclosure from 'components/shared/Disclosure/Disclosure';
import ContributionLimitTable from '../ContributionLimitModal/ContributionLimitTable';

const yearOptions = [
  {
    label: PRIOR_YEAR,
    value: PRIOR_YEAR.toString()
  },
  {
    label: CURRENT_YEAR,
    value: CURRENT_YEAR.toString()
  }
];

const IRASummaryV2 = (props) => {
  const [displayYear, setDisplayYear] = useState(props.defaultTaxYear);

  const controlsRef = useRef(null);
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.addEventListener('beacon-event-input', (event) => {
        const currentValue = event.detail.value;
        changeYear(currentValue);
      });
    }

    return () => {
      if (controlsRef.current) {
        controlsRef.current.removeEventListener('beacon-event-input', (event) => {
          const currentValue = event.detail.value;
          changeYear(currentValue);
        });
      }
    };
  }, []);

  useEffect(() => {
    setDisplayYear(props.defaultTaxYear);
  }, [props]);

  const changeYear = (year) => {
    setDisplayYear(year);
  };
  const { summary, contributionLimit } = props;
  const yearToUse = summary[displayYear];
  const isCurrentYear = displayYear === CURRENT_YEAR.toString();
  const { currentYearMaximumLimit, priorYearMaximumLimit } = contributionLimit;

  return (
    <div className="tile-summary-v2 beacon-on-light">
      <div className="tile-summary-content-v2">
        <div className="tile-summary-header-v2 b-font-weight-bold b-typography-sans-serif--headline-loose--100" slot='title'>
          IRA summary
          <AAMBeaconSegmentedControls scale="mini"
                                      className="tile-summary-toggle-bar-v2"
                                      ref={controlsRef}>
            <p slot="title" className='b-visually-hidden' aria-label={"hidden select year title"}>Select Year</p>
            <AAMBeaconSegmentedControl selected={displayYear === yearOptions[0].value}>
              {yearOptions[0].label}
            </AAMBeaconSegmentedControl>
            <AAMBeaconSegmentedControl selected={displayYear === yearOptions[1].value}>
              {yearOptions[1].label}
            </AAMBeaconSegmentedControl>
          </AAMBeaconSegmentedControls>
          <div className={"display-year-v2 b-font-weight-bold b-typography-sans-serif--body-loose--200"}>{displayYear}</div>
        </div>
        <div className="tile-summary-rows">
          {isCurrentYear && (
            <div className="annual-contribution-row">
                <span
                  className={
                    'b-font-weight-light b-typography-sans-serif--body--100'
                  }>
                  Annual contribution limit{' '}
                </span>
              <Dollar
                className={
                  'b-font-weight-light b-typography-sans-serif--body--100'
                }
                element="span">
                {currentYearMaximumLimit}
              </Dollar>
            </div>
          )}
          {!isCurrentYear && (
            <div className="annual-contribution-row tile-summary-row-text">
                <span
                  className={
                    'b-font-weight-light b-typography-sans-serif--body--100'
                  }>
                  Annual contribution limit{' '}
                </span>
              <Dollar
                className={
                  'b-font-weight-light b-typography-sans-serif--body--100'
                }
                element="span">
                {priorYearMaximumLimit}
              </Dollar>
            </div>
          )}
          <div className="tile-summary-row-text">
              <span className="b-font-weight-bold b-typography-sans-serif--body--100">
                Contributions
              </span>
          </div>
          {yearToUse.contributionList.map((contribution, idx) => {
            const { amount, label } = contribution;
            return (
              label != 'SEP Employee' &&
              label != 'SEP Employer' && (
                <div className="tile-summary-sub-row" key={idx}>
                  <span className={'b-font-weight-light b-typography-sans-serif--body--100'}>
                    {label}
                  </span>
                  <Dollar className={'b-font-weight-light b-typography-sans-serif--body--100'} element="span">
                    {amount}
                  </Dollar>
                </div>
              )
            );
          })}
          {isCurrentYear && (
            <Fragment>
              <div className="tile-summary-row-text tile-summary-row-border tile-summary-spacing">
                  <span className="b-font-weight-bold b-typography-sans-serif--body--100">
                    Distributions
                  </span>
                  {yearToUse.totalDistributions > 0 && (
                    <div className="tile-summary-row tile-summary-row-text">
                      <span className={'b-font-weight-light b-typography-sans-serif--body--100'}>
                        YTD distributions
                      </span>
                      <Dollar className={'b-font-weight-light b-typography-sans-serif--body--100'} element="span">
                        {yearToUse.totalDistributions}
                      </Dollar>
                    </div>
                  )}
              </div>
              <div className="tile-summary-row-text">
                  <span className="b-font-weight-bold b-typography-sans-serif--body--100">
                    Withholding
                  </span>
                  {yearToUse.withholdingList.map((withholding, idx) => {
                    const { amount, label } = withholding;
                    return (
                      <div
                        className="tile-summary-row tile-summary-row-text"
                        key={idx}>
                        <span className={'b-font-weight-light b-typography-sans-serif--body--100'}>
                          {label}
                        </span>
                        <Dollar className={'b-font-weight-light b-typography-sans-serif--body--100'} element="span">
                          {amount}
                        </Dollar>
                      </div>
                    );
                  })}
              </div>
            </Fragment>
          )}
          {!isCurrentYear && (
            <div className="distribution-withholding-prior-year">
              Distributions and withholding are not available online for the
              previous year.
            </div>
          )}
        </div>
        <div className="tile-summary-footer-v2">
          <BeaconDisclosureModal
            linkContent={'Important Information'}
            closeOnClickOut={true}
            size={'Normal'}>
            <Fragment>
              <div className="b-color-text--set--navy b-typography-serif--headline--200">Important Information</div>
              <div className="b-typography-sans-serif--body--200 b-font-weight-light">
                <Disclosure content={Content.IRASummaryDisclosureV2} className={"disclosure-content-v2"} />
              </div>
            </Fragment>
          </BeaconDisclosureModal>
          <BeaconDisclosureModal
            linkContent={'Contribution Limits'}
            closeOnClickOut={true}
            size={'Normal'}>
            <Fragment>
              <div className="b-color-text--set--navy b-typography-serif--headline--200">
                Your IRA Contribution Information
              </div>
              <ContributionLimitTable />
            </Fragment>
          </BeaconDisclosureModal>
        </div>
      </div>
    </div>
)};

IRASummaryV2.propTypes = {
  isDesktopLayout: bool.isRequired,
  isMobileLayout: bool.isRequired,
  contributionLimit: object.isRequired,
  summary: object.isRequired
};

const mapStateToProps = (state) => ({
  defaultTaxYear: getDefaultTaxYear(state),
  isDesktopLayout: isDesktopLayout(state),
  contributionLimit: getIRAContributionLimit(state),
  isMobileLayout: isMobileLayout(state)
});

export default connect(mapStateToProps)(IRASummaryV2);
